import React, { ElementType } from 'react';
import classNames from 'classnames';
import { MarginProps, PaddingProps } from '../_internal/spacing';
import Box, { BoxProps } from '../Box';
import { removeStylingProps } from '../_internal/styling';

import styles from './placeholder.module.scss';

type Variant = 'text' | 'rect' | 'circle';

interface Props {
  /**
   * Type of loading animation. Use false to disable animation.
   * @default 'wave'
   */
  animation?: 'wave' | false;
  /**
   * Shape of the placeholder
   * @default 'text'
   */
  variant?: Variant;
}

const VARIANT_ELEMENT_MAP: Record<Variant, ElementType> = {
  text: 'span',
  rect: 'div',
  circle: 'div',
};

// Include a limited set Box props scoped to common box model props
type PlaceholderBoxProps = Pick<
  BoxProps,
  | 'bgColor'
  | 'display'
  | 'height'
  | 'maxHeight'
  | 'maxWidth'
  | 'minHeight'
  | 'minWidth'
  | 'width'
> &
  MarginProps &
  PaddingProps;

export type PlaceholderProps = Props & PlaceholderBoxProps;

/**
 * Placeholders are used to approximate the layout of the content that is loading.
 * By default they have a “wave” animation applied to them to signify progress.
 */
const Placeholder = ({
  animation = 'wave',
  variant = 'text',
  ...rootProps
}: PlaceholderProps) => {
  const as: ElementType = VARIANT_ELEMENT_MAP[variant] || 'text';

  // Excluding styling props to avoid unintended breaking side effects.
  // These props are omitted in the types but this addition is to prevent non-TS
  // usage.
  const processedRootProps = removeStylingProps(rootProps);

  return (
    <Box
      aria-hidden
      as={as}
      bgColor="gray-95"
      data-testid="placeholder"
      display="block"
      className={classNames(
        styles.container,
        animation ? styles[animation] : undefined,
        styles[variant],
      )}
      {...processedRootProps}
    >
      {variant === 'text' ? <>&nbsp;</> : null}
    </Box>
  );
};

export default Placeholder;
